import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Select,
  Button,
  Table,
  Checkbox,
  Space,
  Typography,
  message,
  Modal,
  Input,
  InputNumber,
  DatePicker,
  Tag,
  Row,
  Col
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { stationOwnerAPI, dataEntryAPI } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Station {
  _id: string;
  name: string;
  isActive: boolean;
  isVerified: boolean;
}

interface Campaign {
  _id: string;
  title: string;
  status: string;
  station: string | { name: string };
}

const BulkOperations: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedStations, setSelectedStations] = useState<string[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [operationType, setOperationType] = useState<string>('');
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchCampaigns();
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const fetchStations = async () => {
    try {
      const data = await stationOwnerAPI.getStations();
      setStations(data);
    } catch (error: any) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const data = await stationOwnerAPI.getCampaigns();
      setCampaigns(data);
    } catch (error: any) {
      message.error('Kampanyalar yüklenemedi');
    }
  };

  const handleBulkStationStatus = async (isActive: boolean) => {
    if (selectedStations.length === 0) {
      message.warning('Lütfen en az bir istasyon seçin');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        selectedStations.map(id =>
          stationOwnerAPI.updateStation(id, { isActive })
        )
      );
      message.success(`${selectedStations.length} istasyon durumu güncellendi`);
      setSelectedStations([]);
      fetchStations();
    } catch (error: any) {
      message.error('İstasyon durumları güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCampaignStatus = async (status: string) => {
    if (selectedCampaigns.length === 0) {
      message.warning('Lütfen en az bir kampanya seçin');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        selectedCampaigns.map(id =>
          stationOwnerAPI.updateCampaign(id, { status })
        )
      );
      message.success(`${selectedCampaigns.length} kampanya durumu güncellendi`);
      setSelectedCampaigns([]);
      fetchCampaigns();
    } catch (error: any) {
      message.error('Kampanya durumları güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPriceUpdate = () => {
    if (selectedStations.length === 0) {
      message.warning('Lütfen en az bir istasyon seçin');
      return;
    }
    setOperationType('price');
    form.resetFields();
    setModalVisible(true);
  };

  const handleBulkCampaignCreate = () => {
    if (selectedStations.length === 0) {
      message.warning('Lütfen en az bir istasyon seçin');
      return;
    }
    setOperationType('campaign');
    form.resetFields();
    setModalVisible(true);
  };

  const handleBulkSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (operationType === 'price') {
        // Toplu fiyat güncelleme
        await Promise.all(
          selectedStations.map(stationId => {
            const priceData: any = {};
            if (values.fuelType && values.fuelPrice) {
              priceData.fuelType = values.fuelType;
              priceData.price = values.fuelPrice;
            }
            if (values.evPrice) {
              priceData.evPrice = values.evPrice;
            }
            return dataEntryAPI.addFuelPrice({ stationId, ...priceData });
          })
        );
        message.success(`${selectedStations.length} istasyonun fiyatları güncellendi`);
      } else if (operationType === 'campaign') {
        // Toplu kampanya oluşturma
        await Promise.all(
          selectedStations.map(stationId =>
            stationOwnerAPI.addCampaign({
              ...values,
              station: stationId,
              startDate: values.dateRange[0].toDate(),
              endDate: values.dateRange[1].toDate()
            })
          )
        );
        message.success(`${selectedStations.length} istasyona kampanya oluşturuldu`);
      }
      setModalVisible(false);
      setSelectedStations([]);
      fetchStations();
      fetchCampaigns();
    } catch (error: any) {
      message.error('Toplu işlem başarısız oldu');
    } finally {
      setLoading(false);
    }
  };

  const stationColumns = [
    {
      title: 'Seç',
      key: 'select',
      width: 60,
      render: (_: any, record: Station) => (
        <Checkbox
          checked={selectedStations.includes(record._id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedStations([...selectedStations, record._id]);
            } else {
              setSelectedStations(selectedStations.filter(id => id !== record._id));
            }
          }}
        />
      )
    },
    {
      title: 'İstasyon Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'Doğrulama',
      dataIndex: 'isVerified',
      key: 'isVerified',
      render: (verified: boolean) => (
        <Tag color={verified ? 'success' : 'warning'}>
          {verified ? 'Doğrulanmış' : 'Beklemede'}
        </Tag>
      )
    }
  ];

  const campaignColumns = [
    {
      title: 'Seç',
      key: 'select',
      width: 60,
      render: (_: any, record: Campaign) => (
        <Checkbox
          checked={selectedCampaigns.includes(record._id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedCampaigns([...selectedCampaigns, record._id]);
            } else {
              setSelectedCampaigns(selectedCampaigns.filter(id => id !== record._id));
            }
          }}
        />
      )
    },
    {
      title: 'Kampanya Adı',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'İstasyon',
      dataIndex: 'station',
      key: 'station',
      render: (station: string | { name: string }) => 
        typeof station === 'object' ? station.name : station
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          active: { color: 'success', text: 'Aktif' },
          pending: { color: 'warning', text: 'Beklemede' },
          expired: { color: 'default', text: 'Süresi Dolmuş' },
          rejected: { color: 'error', text: 'Reddedildi' }
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      }
    }
  ];

  const tabItems = [
    {
      key: 'stations',
      label: 'İstasyonlar',
      children: (
        <div>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto', marginBottom: 16 }} size="small">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleBulkStationStatus(true)}
              disabled={selectedStations.length === 0 || loading}
              block={isMobile}
            >
              Seçilenleri Aktif Et ({selectedStations.length})
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={() => handleBulkStationStatus(false)}
              disabled={selectedStations.length === 0 || loading}
              block={isMobile}
            >
              Seçilenleri Pasif Et ({selectedStations.length})
            </Button>
            <Button
              icon={<DollarOutlined />}
              onClick={handleBulkPriceUpdate}
              disabled={selectedStations.length === 0 || loading}
              block={isMobile}
            >
              Toplu Fiyat Güncelle ({selectedStations.length})
            </Button>
            <Button
              icon={<GiftOutlined />}
              onClick={handleBulkCampaignCreate}
              disabled={selectedStations.length === 0 || loading}
              block={isMobile}
            >
              Toplu Kampanya Oluştur ({selectedStations.length})
            </Button>
            {selectedStations.length > 0 && (
              <Button
                onClick={() => setSelectedStations([])}
                block={isMobile}
              >
                Seçimi Temizle
              </Button>
            )}
          </Space>
          <Table
            columns={stationColumns}
            dataSource={stations}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              responsive: true
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      )
    },
    {
      key: 'campaigns',
      label: 'Kampanyalar',
      children: (
        <div>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto', marginBottom: 16 }} size="small">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleBulkCampaignStatus('active')}
              disabled={selectedCampaigns.length === 0 || loading}
              block={isMobile}
            >
              Seçilenleri Aktif Et ({selectedCampaigns.length})
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={() => handleBulkCampaignStatus('expired')}
              disabled={selectedCampaigns.length === 0 || loading}
              block={isMobile}
            >
              Seçilenleri Sonlandır ({selectedCampaigns.length})
            </Button>
            {selectedCampaigns.length > 0 && (
              <Button
                onClick={() => setSelectedCampaigns([])}
                block={isMobile}
              >
                Seçimi Temizle
              </Button>
            )}
          </Space>
          <Table
            columns={campaignColumns}
            dataSource={campaigns}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              responsive: true
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            Toplu İşlemler
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            Birden fazla istasyon veya kampanya üzerinde toplu işlemler yapın
          </Text>
        </Col>
      </Row>

      <Card>
        <Tabs items={tabItems} />
      </Card>

      <Modal
        title={
          operationType === 'price' 
            ? 'Toplu Fiyat Güncelleme' 
            : 'Toplu Kampanya Oluşturma'
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 600}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleBulkSubmit}
        >
          {operationType === 'price' && (
            <>
              <Form.Item
                name="fuelType"
                label="Yakıt Tipi"
              >
                <Select placeholder="Yakıt tipi seçin">
                  <Option value="benzin">Benzin</Option>
                  <Option value="dizel">Dizel</Option>
                  <Option value="lpg">LPG</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="fuelPrice"
                label="Yakıt Fiyatı (₺)"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="Fiyat girin"
                />
              </Form.Item>
              <Form.Item
                name="evPrice"
                label="EV Şarj Fiyatı (₺/kWh)"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="Fiyat girin"
                />
              </Form.Item>
            </>
          )}

          {operationType === 'campaign' && (
            <>
              <Form.Item
                name="title"
                label="Kampanya Başlığı"
                rules={[{ required: true, message: 'Başlık gerekli' }]}
              >
                <Input placeholder="Kampanya başlığı" />
              </Form.Item>
              <Form.Item
                name="description"
                label="Açıklama"
              >
                <Input.TextArea rows={4} placeholder="Kampanya açıklaması" />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="discountPercentage"
                    label="İndirim Oranı (%)"
                    rules={[{ required: true, message: 'İndirim oranı gerekli' }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      style={{ width: '100%' }}
                      placeholder="İndirim %"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="category"
                    label="Kategori"
                    rules={[{ required: true, message: 'Kategori seçin' }]}
                  >
                    <Select placeholder="Kategori seçin">
                      <Option value="fuel">Yakıt</Option>
                      <Option value="market">Market</Option>
                      <Option value="carwash">Oto Yıkama</Option>
                      <Option value="restaurant">Restoran</Option>
                      <Option value="atm">ATM</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="dateRange"
                label="Tarih Aralığı"
                rules={[{ required: true, message: 'Tarih aralığı seçin' }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Text type="secondary">
              Bu işlem {selectedStations.length} istasyona uygulanacak
            </Text>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Uygula
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BulkOperations;

